import React, { useState, useRef, useEffect } from 'react';

const SearchableMultiSelect = ({ 
  options, 
  value = [], 
  selectedValues = [], // Support both value and selectedValues props
  onChange, 
  placeholder = "Search and select...",
  label,
  required = false,
  groupedOptions = null,
  multiSelect = true, // Allow multiple selections by default
  allowCustom = false // Allow custom values to be typed
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const dropdownRef = useRef(null);
  
  // Use selectedValues if provided, otherwise use value
  const currentValues = selectedValues.length > 0 ? selectedValues : value;

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
        // Handle custom value when clicking outside
        if (allowCustom && searchTerm.trim() && !currentValues.includes(searchTerm.trim())) {
          if (multiSelect) {
            onChange([...currentValues, searchTerm.trim()]);
          } else {
            onChange([searchTerm.trim()]);
          }
          setSearchTerm('');
        }
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [allowCustom, searchTerm, currentValues, multiSelect, onChange]);

  const handleToggle = (optionValue) => {
    if (multiSelect) {
      if (currentValues.includes(optionValue)) {
        onChange(currentValues.filter(v => v !== optionValue));
      } else {
        onChange([...currentValues, optionValue]);
      }
    } else {
      // Single select mode
      onChange([optionValue]);
      setIsOpen(false);
      setSearchTerm('');
    }
  };

  const handleRemove = (optionValue) => {
    onChange(currentValues.filter(v => v !== optionValue));
  };
  
  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && allowCustom && searchTerm.trim()) {
      e.preventDefault();
      const customValue = searchTerm.trim();
      if (!currentValues.includes(customValue)) {
        if (multiSelect) {
          onChange([...currentValues, customValue]);
        } else {
          onChange([customValue]);
          setIsOpen(false);
        }
      }
      setSearchTerm('');
    }
  };

  const getFilteredOptions = () => {
    const search = searchTerm.toLowerCase();
    
    if (groupedOptions) {
      return groupedOptions.map(group => ({
        ...group,
        options: group.options.filter(opt => 
          opt.toLowerCase().includes(search)
        )
      })).filter(group => group.options.length > 0);
    }
    
    return options.filter(opt => opt.toLowerCase().includes(search));
  };

  const filteredOptions = getFilteredOptions();

  return (
    <div className="relative" ref={dropdownRef}>
      {label && (
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
          {label} {required && '*'}
        </label>
      )}
      
      {/* Selected items display */}
      {currentValues.length > 0 && (
        <div className="flex flex-wrap gap-2 mb-2">
          {currentValues.map((item, i) => (
            <span 
              key={i} 
              className="px-3 py-1.5 bg-blue-100 text-blue-700 rounded-md text-sm flex items-center gap-2 font-medium"
            >
              {item}
              <button
                type="button"
                onClick={() => handleRemove(item)}
                className="hover:text-red-600 font-bold text-lg leading-none"
              >
                ×
              </button>
            </span>
          ))}
        </div>
      )}

      {/* Search input */}
      <div className="relative">
        <input
          type="text"
          value={searchTerm}
          onChange={(e) => {
            setSearchTerm(e.target.value);
            setIsOpen(true);
          }}
          onFocus={() => setIsOpen(true)}
          onKeyDown={handleKeyDown}
          placeholder={placeholder}
          className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary dark:bg-gray-700 dark:text-white"
        />
        <div className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400">
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
          </svg>
        </div>
      </div>

      {/* Dropdown */}
      {isOpen && (
        <div className="absolute z-50 w-full mt-1 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-lg shadow-lg max-h-64 overflow-y-auto">
          {filteredOptions.length === 0 ? (
            <div className="px-4 py-3 text-gray-500 dark:text-gray-400 text-sm">
              {allowCustom && searchTerm.trim() ? (
                <button
                  onClick={() => {
                    const customValue = searchTerm.trim();
                    if (!currentValues.includes(customValue)) {
                      if (multiSelect) {
                        onChange([...currentValues, customValue]);
                      } else {
                        onChange([customValue]);
                        setIsOpen(false);
                      }
                    }
                    setSearchTerm('');
                  }}
                  className="w-full text-left hover:bg-gray-100 dark:hover:bg-gray-700 px-2 py-1 rounded"
                >
                  Add "{searchTerm.trim()}"
                </button>
              ) : (
                'No options found'
              )}
            </div>
          ) : groupedOptions ? (
            filteredOptions.map((group, groupIdx) => (
              <div key={groupIdx}>
                <div className="px-4 py-2 text-xs font-semibold text-gray-500 dark:text-gray-400 bg-gray-50 dark:bg-gray-700/50 sticky top-0">
                  {group.label}
                </div>
                {group.options.map((option, optIdx) => (
                  <div
                    key={optIdx}
                    onClick={() => handleToggle(option)}
                    className={`px-4 py-2 cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-700 flex items-center gap-2 ${
                      currentValues.includes(option) ? 'bg-primary/5' : ''
                    }`}
                  >
                    {multiSelect && (
                      <input
                        type="checkbox"
                        checked={currentValues.includes(option)}
                        onChange={() => {}}
                        className="rounded text-primary focus:ring-primary"
                      />
                    )}
                    <span className="text-gray-900 dark:text-white">{option}</span>
                  </div>
                ))}
              </div>
            ))
          ) : (
            filteredOptions.map((option, idx) => (
              <div
                key={idx}
                onClick={() => handleToggle(option)}
                className={`px-4 py-2 cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-700 flex items-center gap-2 ${
                  currentValues.includes(option) ? 'bg-primary/5' : ''
                }`}
              >
                {multiSelect && (
                  <input
                    type="checkbox"
                    checked={currentValues.includes(option)}
                    onChange={() => {}}
                    className="rounded text-primary focus:ring-primary"
                  />
                )}
                <span className="text-gray-900 dark:text-white">{option}</span>
              </div>
            ))
          )}
        </div>
      )}
    </div>
  );
};

export default SearchableMultiSelect;
